import React, {useState, useRef, useEffect} from "react";
import { Wrapper } from "@googlemaps/react-wrapper"
import {
    PerspectiveCamera,
    Scene,
    AmbientLight,
    WebGLRenderer,
    Matrix4,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import ThreejsOverlayView from "@ubilabs/threejs-overlay-view";
import { CatmullRomCurve3 } from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";

import CustomMarker from "./CustomMarker";
import {mapFunctions} from "../map/mapFunctions";
import {jltfAssets} from "../../assets/3d/jltf/jltfLibrary";

let mapOptions = {
    mapId: '22dc9c2af85d13f8',
    // center: { lat: 20.581465, lng: -87.120603 },
    center: { lat: 0.0, lng: 0.0 },
    zoom: 18,
    streetViewControl: false,
    disableDefaultUI: false,
    gestureHandling: "cooperative",
    heading: 0,
    tilt: 0,
};

export default function Map3D ( { markers, routes, routAB, pointA, pointB, googleConfig, options, onTraceClick, onMapClick, onRightClick , onMarkerClick, className = '' } ) {


    useEffect(() => {
        if (googleConfig?.mapOptions) {
            mapOptions.center = googleConfig?.mapOptions.center;

            if (googleConfig?.mapOptions.streetViewControl) {
                mapOptions.streetViewControl = googleConfig?.mapOptions.streetViewControl;
            }

            if (googleConfig?.mapOptions.disableDefaultUI) {
                mapOptions.disableDefaultUI = googleConfig?.mapOptions.disableDefaultUI;
            }
        }
    }, [ googleConfig ]);

    return (<div className={className}>
        <Wrapper apiKey={'AIzaSyA-RFluygEbx-lKv3wShH-hx_c_2F95Dbk'} version="beta" libraries={["marker"] }>
            <WebGLMap
                markers={markers}
                routes={routes}
                routAB={routAB}
                pointA={pointA}
                pointB={pointB}
                options={options}
                onTraceClick={onTraceClick}
                onMapClick={onMapClick}
                onRightClick={onRightClick}
                onMarkerClick={onMarkerClick}
            />
        </Wrapper>
    </div>
    );
}


function WebGLMap( { markers, routes, routAB, pointA, pointB, options, onTraceClick, onMapClick, onRightClick, onMarkerClick } ) {
    const overlayRef = useRef();
    const [map, setMap] = useState();
    const ref = useRef();

    useEffect(() => {
        if (!overlayRef.current) {
            if (mapOptions.center.lat === 0.0 && mapOptions.center.lng === 0.0) {
                return;
            }
            const _map = new window.google.maps.Map(ref.current, mapOptions);
            _map.addListener('click', onMapClick);
            _map.addListener('rightclick', onRightClick);
            setMap(_map);
            overlayRef.current = createOverlay(_map, options);
            // overlayRef.current.addListener('click', onMapClick);
        }
    }, [markers, routes, routAB, options]);

    return (<>
            <div ref={ref} id="map" key={'map'} className={'map3d_container'}/>
            {map && options.showRoutes && routes &&
                routes.map(_route => {
                    return <CreateRoute map={map} route={_route} routes={routes} />
                })
                }
            {map && markers && <CreateMarkers map={map} markers={markers} onMarkerClick={onMarkerClick} showMarker={options.showActivities} />}
            {map && routes && <CreateRouteMarkers map={map} routes={routes} onClick={onTraceClick} showMarker={options.showRouteMarkers}  />}
            {map && pointA && <CreatePointMarkers map={map} point={{...pointA, name : 'Desde'}} color={'red'} onClick={onTraceClick} showMarker={true} />}
            {map && pointB && <CreatePointMarkers map={map} point={{...pointB, name : 'Hasta'}} color={'blue'} onClick={onTraceClick} showMarker={true}  />}
            {map && options.showEasyPath && routAB && <CreateRoute map={map} route={routAB} /> }
        </>);
}


function createOverlay(map, options) {
    const overlay = new window.google.maps.WebGLOverlayView();
    let renderer, scene, camera, loader;

    overlay.onAdd = () => {
        scene = new Scene();
        camera = new PerspectiveCamera();
        const light = new AmbientLight(0xffffff, 0.9);
        scene.add(light);

        loader = new GLTFLoader();

        if (options?.show3D) {
            const jltfAsset = jltfAssets[options?.udn?.code?.toLowerCase()];

            loader.loadAsync(jltfAsset.asset).then((object) => {
                const group = object.scene;
                group.scale.setScalar(jltfAsset.scalar);
                group.rotation.set(Math.PI / 2, 0, 0);

                if (jltfAsset.position.x) {
                    group.position.setX(jltfAsset.position.x);
                }

                if (jltfAsset.position.y) {
                    group.position.setY(jltfAsset.position.y);
                }

                if (jltfAsset.position.z) {
                    group.position.setZ(jltfAsset.position.z);
                }

                scene.add(group);
            });
        }


        // loader.loadAsync(asset_3d_hacienda).then((object) => {
        //     const group = object.scene;
        //     group.scale.setScalar(.05);
        //     group.rotation.set(Math.PI / 2, Math.PI / 2, 0); // X, Y Z
        //     group.position.setZ(-130);
        //     group.position.setY(+90);
        //     group.position.setX(+25);
        //     scene.add(group);
        // });
    };

    overlay.onContextRestored = ({ gl }) => {
        renderer = new WebGLRenderer({
            canvas: gl.canvas,
            context: gl,
            ...gl.getContextAttributes(),
        });
        renderer.autoClear = false;

        loader.manager.onLoad = () => {
            renderer.setAnimationLoop(() => {
                // map.moveCamera({
                //     tilt: mapOptions.tilt,
                //     heading: mapOptions.heading,
                //     zoom: mapOptions.zoom,
                // });
                //
                // if (mapOptions.tilt < 60) {
                //     mapOptions.tilt += 0.5;
                // } else if (mapOptions.zoom < 20) {
                //     mapOptions.zoom += 0.05;
                // } else if (mapOptions.heading < 125) {
                //     mapOptions.heading += 0.5;
                // } else {
                //     renderer.setAnimationLoop(null);
                // }
                renderer.setAnimationLoop(null);
            });
        };
    };

    overlay.onDraw = ({ transformer }) => {
        const matrix = transformer.fromLatLngAltitude({
            lat: mapOptions.center.lat,
            lng: mapOptions.center.lng,
            altitude: 120,
        });
        camera.projectionMatrix = new Matrix4().fromArray(matrix);

        overlay.requestRedraw();
        renderer.render(scene, camera);
        renderer.resetState();
    };

    overlay.setMap(map);

    return overlay;
}

function CreatePointMarkers ({ map, point, color, showMarker, onClick }) {

    return (<>
            <CustomMarker
                key={ point.lat + '-' + point.lng }
                map={map}
                position={ point }
                showMarker={ showMarker }
                onClick={() => onClick(point)}
            >
                    {point.name}
                <i className={"fa-solid fa-droplet fa-rotate-180 marker-"+ color}/>
                {/*<div key={'div' + point.lat + '-' + point.lng} className={'selection-marker marker-' + color}>*/}
                {/*</div>*/}
            </CustomMarker>
        </>
    );
}

function CreateRouteMarkers ({ map, routes, onClick, showMarker }) {

    return (<>
            {
                routes && routes.map((_rout) => {
                    return (<>
                        <CustomMarker
                            key={_rout.code}
                            map={map}
                            position={ mapFunctions.getMidElement( _rout.points ) }
                            showMarker={ showMarker }
                            onClick={() => onClick(_rout)}
                        >
                            <div key={'div' + _rout.code} className={'rout-marker'} >
                            </div>
                        </CustomMarker>
                    </>);
                })
            }
        </>
    );
}

function CreateMarkers ({ map, markers, onMarkerClick, showMarker }) {
    const [_markers, setMarkers] = useState([]);

    useEffect(() => {
        setMarkers(markers);
    }, [markers]);

    const onThisClick = (_marker) => {
        const result = _markers.map((m) => {
            return {
                ...m,
                selected: m.id === _marker.id
            }
        });

        setMarkers(result);
        onMarkerClick(_marker);
    }

    return (<>
        {
            _markers && _markers.map((_marker) => {
                return (<>
                        <CustomMarker
                            key={_marker.code}
                            map={map}
                            position={_marker.position}
                            showMarker={showMarker }
                            onClick={() => onThisClick(_marker)}
                        >
                            <div className={'activity-marker' + (_marker.selected ? '-selected' : '')}>
                                <div className={'activity-marker-number'}>{_marker.code}</div>
                                <div className={'activity-marker-name'}>{_marker.name}</div>
                                { _marker.routeName && <div className={'activity-marker-route'}>{_marker.routeName}</div> }
                                { _marker.categoryName && <div className={'activity-marker-category'}>{_marker.categoryName}</div>}
                            </div>
                        </CustomMarker>
                </>);
            })
        }
        </>
    );
}

function CreateRoute({ route, map }) {
    const overlayRef = useRef();
    const trackRef = useRef();
    const carRef = useRef();

    useEffect(() => {
        //map.setCenter(route[Math.floor(route.length / 2)], 17);

        if (!overlayRef.current) {
            overlayRef.current = new ThreejsOverlayView(mapOptions.center);
            overlayRef.current.setMap(map);
        }

        if ( !route?.points.length  )
            return ;

        if ( route?.points.length < 2 )
            return ;

        const scene = overlayRef.current.getScene();

        const points = route?.points?.map((p) => overlayRef.current.latLngAltToVector3(p));
        const curve = new CatmullRomCurve3(points);

        if (trackRef.current) {
            scene.remove(trackRef.current);
        }
        trackRef.current = createTrackFromCurve(curve, route.color);
        scene.add(trackRef.current);

        // MODEL TO ANIMATE
        // loadModel().then((model) => {
        //     if (carRef.current) {
        //         scene.remove(carRef.current);
        //     }
        //     carRef.current = model;
        //     scene.add(carRef.current);
        // });

        overlayRef.current.update = () => {
            trackRef.current.material.resolution.copy(
                overlayRef.current.getViewportSize()
            );

            // TRACK MODEL TO PATH
            // if (carRef.current) {
            //     const progress = (performance.now() % ANIMATION_MS) / ANIMATION_MS;
            //     curve.getPointAt(progress, carRef.current.position);
            //     carRef.current.quaternion.setFromUnitVectors(
            //         FRONT_VECTOR,
            //         curve.getTangentAt(progress)
            //     );
            //     carRef.current.rotateX(Math.PI / 2);
            // }

            overlayRef.current.requestRedraw();
        };

        return () => {
            scene.remove(trackRef.current);
            scene.remove(carRef.current);
        };
    }, [route]);

    return null;
}

function createTrackFromCurve(curve, color) {
    const points = curve.getSpacedPoints(curve.points.length * 10);
    const positions = points.map((point) => point.toArray()).flat();

    return new Line2(
        new LineGeometry().setPositions(positions),
        new LineMaterial({
            color: color,
            linewidth: 5,
        })
    );
}